import { useState } from "react";
import { Outlet, Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Menu, X } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { BackButton } from "@/components/BackButton";
import { Logo } from "@/components/Logo";
import { StatusMessage } from "@/components/StatusMessage";
import { Breadcrumb } from "@/components/Breadcrumb";
import { SideNav, HelpBlock, UserBox } from "@/components/ShellParts";

const useShellData = (user) => {
  const { data: convos = [] } = useQuery({ queryKey: ["enquiries"], queryFn: async () => (await api.get("/enquiries")).data, enabled: !!user, refetchInterval: 30000 });
  const { data: stats } = useQuery({ queryKey: ["stats"], queryFn: async () => (await api.get("/stats")).data, enabled: !!user, refetchInterval: 30000 });
  const unread = user ? convos.reduce((n, c) => n + c.unread, 0) : 0;
  return { badges: { messages: unread, approvals: (stats?.resolutions_pending || 0) + (stats?.quotes_new || 0) + (stats?.media_pending || 0) }, openCount: stats?.tickets_open || 0 };
};

const TopBar = ({ open, onToggle, admin }) => (
  <header className="sticky top-0 z-40 border-b border-slate-800 bg-slate-950/85 backdrop-blur-xl" data-testid="topbar">
    <div className="flex h-14 items-center gap-3 px-4 lg:px-8">
      <button data-testid="mobile-menu-toggle" onClick={onToggle} className="rounded-md p-2 text-slate-400 hover:bg-slate-800 md:hidden">
        {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
      </button>
      <BackButton />
      <Link to="/" data-testid="topbar-home-link" className="hidden text-sm text-slate-400 hover:text-white sm:block">Home</Link>
      <StatusMessage admin={admin} />
    </div>
    <div className="flex h-9 items-center justify-center border-t border-slate-800/70 px-4" data-testid="topbar-title-row">
      <Breadcrumb prefix="topbar" />
    </div>
  </header>
);

export default function AppShell() {
  const { user, logout } = useAuth();
  const [open, setOpen] = useState(false);
  const { badges, openCount } = useShellData(user);
  const close = () => setOpen(false);
  const sidebar = (
    <div className="flex h-full flex-col justify-between gap-6 overflow-y-auto p-4">
      <div className="space-y-8">
        <Logo testId="sidebar-logo-link" tagline={false} siteLink />
        <SideNav user={user} badges={badges} onNavigate={close} />
        <HelpBlock user={user} openCount={openCount} onNavigate={close} />
      </div>
      <UserBox user={user} logout={logout} />
    </div>
  );
  return (
    <div className="min-h-screen bg-slate-950 text-slate-200" data-testid="app-shell">
      <aside className="fixed inset-y-0 left-0 z-30 hidden w-64 border-r border-slate-800 bg-slate-900 md:block">{sidebar}</aside>
      {open && (
        <div className="fixed inset-0 z-50 md:hidden">
          <div className="absolute inset-0 bg-black/60" onClick={close} />
          <aside className="absolute inset-y-0 left-0 w-64 border-r border-slate-800 bg-slate-900">{sidebar}</aside>
        </div>
      )}
      <div className="md:pl-64">
        <TopBar open={open} onToggle={() => setOpen((o) => !o)} admin={user?.role === "admin"} />
        <main className="mx-auto max-w-6xl px-4 py-8 sm:px-6 lg:px-8 lg:py-12">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
